import { z } from 'zod';
import { supplierFactorObjectIdV1Schema } from './supplierFactorContracts';
import { supplierFactorDraftV1Schema } from './supplierDraftContracts';

// JS zod mirror of backend/src/contracts/supplierSourceUpload.ts. Kept
// byte-for-byte faithful to the backend schemas so the FE parses every
// response through the same shape the backend serialises.
//
// AMENDMENT (S7.4): POST /supplier/uploads is SYNCHRONOUS — ingestion
// (quarantine → hostile-file boundary → parse → validate → draft creation)
// happens inside the request/response cycle. There is no polling flow; the
// 201 response already carries the produced drafts.

export const SupplierSourceUploadValidationState = Object.freeze({
  Quarantined: 'quarantined',
  Validated: 'validated',
  Rejected: 'rejected',
});

export const SUPPLIER_SOURCE_UPLOAD_REJECTION_CODES = Object.freeze([
  'FILE_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'SIGNATURE_MISMATCH',
  'INVALID_ARCHIVE',
  'ARCHIVE_LIMIT_EXCEEDED',
  'MACRO_ENABLED',
  'EXTERNAL_LINK',
  'DANGEROUS_FORMULA',
  'WORKBOOK_LIMIT_EXCEEDED',
  'PARSER_TIMEOUT',
  'PARSER_BUSY',
  'UNSUPPORTED_TEMPLATE',
  'PARSE_FAILED',
  'PARTIAL_EXTRACTION',
  'INVALID_INSTALLATION',
  // S10 (D-S10-4) — every rejection path returns the versioned shape.
  'INVALID_REQUEST',
  'INTERNAL_ERROR',
  // template-upload-error-recovery S3 — both carry `issues`.
  'REPORTING_PERIOD_REQUIRED', // resend the same file with `reportingPeriod`
  'TEMPLATE_DATA_INVALID', // the workbook itself has to be corrected
]);

export const supplierSourceUploadRejectionCodeV1Schema = z.enum(
  SUPPLIER_SOURCE_UPLOAD_REJECTION_CODES,
);

export const supplierSourceUploadRequestV1Schema = z
  .object({
    version: z.literal(1),
    // Optional (additive, S10 backend amendment) — when omitted, the backend
    // infers the installation from the template's own extracted entity data
    // (matching an existing installation or creating one); an explicit id
    // always takes precedence. See `resolvedInstallation` on the projected
    // upload for which one received the drafts.
    installationId: supplierFactorObjectIdV1Schema.optional(),
    // Optional (additive) — the period for a template that states none of its
    // own. Ignored when the workbook's period is readable.
    reportingPeriod: z
      .object({ from: z.string().datetime(), to: z.string().datetime() })
      .strict()
      .optional(),
  })
  .strict();

// The wire shape of a Template Issue (backend templateIssues.ts). `code` is a
// plain string: the issue registry grows on its own schedule, and a new issue
// code must not turn a rejection into a response-validation failure.
export const supplierTemplateIssueV1Schema = z
  .object({
    code: z.string().min(1),
    path: z.array(z.union([z.string(), z.number()])),
    message: z.string().min(1),
    params: z
      .record(
        z.union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))]),
      )
      .optional(),
    severity: z.enum(['error', 'warning']),
    scope: z.enum(['file', 'row']),
    resolution: z.enum(['reportingPeriod', 'indirectChoice', 'duplicateChoice']).optional(),
  })
  .strict();

export const supplierSourceUploadResolvedInstallationV1Schema = z
  .object({
    id: supplierFactorObjectIdV1Schema,
    name: z.string().min(1),
    // "explicit" — the caller supplied installationId. "matched" — inferred
    // from the template and matched an existing installation by normalized
    // name. "created" — no match existed, so a new installation was created
    // from the extracted name/country.
    resolution: z.enum(['explicit', 'matched', 'created']),
  })
  .strict();

export const supplierSourceUploadV1Schema = z
  .object({
    version: z.literal(1),
    id: supplierFactorObjectIdV1Schema,
    originalFilename: z.string().min(1),
    fileSize: z.number().int().nonnegative(),
    checksumSha256: z.string().regex(/^[a-f\d]{64}$/),
    suppliedMimeType: z.string().min(1),
    validatedType: z.literal('xlsx').optional(),
    parser: z
      .object({
        type: z.enum(['unknown', 'efda', 'comms']),
        version: z.string().min(1),
      })
      .strict(),
    validationState: z.nativeEnum(SupplierSourceUploadValidationState),
    validationError: z
      .object({
        code: supplierSourceUploadRejectionCodeV1Schema,
        message: z.string().min(1),
        issues: z.array(supplierTemplateIssueV1Schema).optional(),
      })
      .strict()
      .optional(),
    validationMetrics: z
      .object({
        archiveEntryCount: z.number().int().nonnegative(),
        decompressedBytes: z.number().int().nonnegative(),
        formulaCellsNeutralized: z.number().int().nonnegative(),
      })
      .strict()
      .optional(),
    sourceDiagramId: supplierFactorObjectIdV1Schema.optional(),
    // Set once, at the same moment as `sourceDiagramId`/`proposalIds` (i.e.
    // only on a successful validated upload) — absent on quarantined/rejected
    // uploads.
    resolvedInstallation: supplierSourceUploadResolvedInstallationV1Schema.optional(),
    proposalIds: z.array(supplierFactorObjectIdV1Schema),
    retention: z
      .object({
        retainUntil: z.string().datetime().optional(),
        erasureRequestedAt: z.string().datetime().optional(),
        erasedAt: z.string().datetime().optional(),
      })
      .strict(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();

export const supplierSourceDiagramV1Schema = z
  .object({
    id: supplierFactorObjectIdV1Schema,
    sourceUploadId: supplierFactorObjectIdV1Schema,
    origin: z.literal('supplier_source_upload'),
    readOnly: z.literal(true),
  })
  .strict();

export const supplierSourceUploadListResponseV1Schema = z
  .object({ version: z.literal(1), items: z.array(supplierSourceUploadV1Schema) })
  .strict();

export const supplierSourceUploadDetailResponseV1Schema = z
  .object({
    version: z.literal(1),
    upload: supplierSourceUploadV1Schema,
    diagram: supplierSourceDiagramV1Schema.optional(),
    drafts: z.array(supplierFactorDraftV1Schema),
    // Only on the ingest response: what the parser assumed while reading a
    // template it accepted.
    warnings: z.array(supplierTemplateIssueV1Schema).optional(),
  })
  .strict();

export const supplierSourceUploadRejectedResponseV1Schema = z
  .object({
    version: z.literal(1),
    error: z
      .object({
        code: supplierSourceUploadRejectionCodeV1Schema,
        message: z.string().min(1),
        issues: z.array(supplierTemplateIssueV1Schema).optional(),
      })
      .strict(),
    upload: supplierSourceUploadV1Schema.optional(),
  })
  .strict();
